// =============================================================================
// clientes/src/lib/carrinho.ts — o carrinho do parceiro, puro e testado.
//
// O preço aqui é SÓ EXIBIÇÃO: quem congela o preço de verdade é o servidor
// (criar_pedido_b2b recebe apenas product_id + quantidade). Se o catálogo
// mudar entre o carrinho e o confirmar, vale o do servidor — e é por isso que
// paraItensDePedido() joga fora tudo menos id e quantidade.
// =============================================================================

export interface ItemCarrinho {
  product_id: string;
  sku: string;
  descricao: string;
  /** O preço do catálogo no momento em que entrou — exibição, não contrato. */
  preco: number;
  pronta_entrega: boolean;
  quantidade: number;
}

const CHAVE = 'serginho-clientes-carrinho';
export const QTD_MAXIMA = 999;
export const ITENS_MAXIMOS = 100;

const clamp = (n: number) => Math.min(QTD_MAXIMA, Math.max(1, Math.round(n)));

/** Soma no existente em vez de duplicar a linha. */
export function adicionar(lista: ItemCarrinho[], item: ItemCarrinho): ItemCarrinho[] {
  const existente = lista.find((i) => i.product_id === item.product_id);
  if (existente) {
    return lista.map((i) => i.product_id === item.product_id
      ? { ...i, quantidade: clamp(i.quantidade + item.quantidade) }
      : i);
  }
  if (lista.length >= ITENS_MAXIMOS) return lista;
  return [...lista, { ...item, quantidade: clamp(item.quantidade) }];
}

export function mudarQuantidade(lista: ItemCarrinho[], productId: string, quantidade: number): ItemCarrinho[] {
  return lista.map((i) => i.product_id === productId
    ? { ...i, quantidade: clamp(quantidade) }
    : i);
}

export function remover(lista: ItemCarrinho[], productId: string): ItemCarrinho[] {
  return lista.filter((i) => i.product_id !== productId);
}

export const totalDoCarrinho = (lista: ItemCarrinho[]): number =>
  lista.reduce((s, i) => s + i.preco * i.quantidade, 0);

export const temSobEncomenda = (lista: ItemCarrinho[]): boolean =>
  lista.some((i) => !i.pronta_entrega);

/** O payload da RPC: SÓ id e quantidade — o resto é assunto do servidor. */
export const paraItensDePedido = (lista: ItemCarrinho[]) =>
  lista.map((i) => ({ product_id: i.product_id, quantidade: i.quantidade }));

// --- persistência no aparelho -------------------------------------------------

export function lerCarrinho(): ItemCarrinho[] {
  try {
    const bruto = localStorage.getItem(CHAVE);
    const lista = bruto ? JSON.parse(bruto) as unknown : [];
    if (!Array.isArray(lista)) return [];
    return lista.filter((x): x is ItemCarrinho =>
      typeof x === 'object' && x !== null
      && typeof (x as ItemCarrinho).product_id === 'string'
      && typeof (x as ItemCarrinho).quantidade === 'number');
  } catch {
    return [];
  }
}

export function salvarCarrinho(lista: ItemCarrinho[]): void {
  try { localStorage.setItem(CHAVE, JSON.stringify(lista)); } catch { /* cheio */ }
}

export function limparCarrinho(): void {
  try { localStorage.removeItem(CHAVE); } catch { /* nada */ }
}
